/**
 * PostHog client para las landing pages (Next.js).
 *
 * Se ejecuta junto a GA4 tras el consentimiento: unifica el embudo
 * landing → registro → uso de la webapp bajo el MISMO project de PostHog que usa
 * Backend-webapp (src/lib/posthog.ts), para poder seguir el recorrido completo.
 *
 * La project key de PostHog es pública (phc_...), así que es seguro exponerla como
 * NEXT_PUBLIC_*. Se dejan valores por defecto = los mismos que la webapp, y se pueden
 * sobreescribir por env sin tocar código.
 */

import { hasAnalyticsConsent } from './cookie-consent';

// Mismo project + host que Backend-webapp/src/lib/posthog.ts (para unificar el embudo).
const POSTHOG_KEY =
  process.env.NEXT_PUBLIC_POSTHOG_KEY ?? 'phc_2YD2JHMwArdFYOJld0deiMZCZkQyeXYW7zxBc8n5hoK';
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://eu.i.posthog.com';

type PostHogClient = typeof import('posthog-js')['default'];
type PendingEvent = { event: string; properties?: Record<string, unknown> };

let client: PostHogClient | null = null;
let loading: Promise<PostHogClient> | null = null;
const pendingEvents: PendingEvent[] = [];

async function loadPostHog() {
  if (client) {
    if (hasAnalyticsConsent()) client.opt_in_capturing();
    return client;
  }
  if (loading) return loading;

  const task = import('posthog-js').then(({ default: posthog }) => {
    if (!hasAnalyticsConsent()) return posthog;
    if (!posthog.__loaded) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: true,
        disable_session_recording: true,
        persistence: 'localStorage',
        respect_dnt: true,
        before_send: (event) => (hasAnalyticsConsent() ? event : null),
        loaded: (ph) => {
          (window as unknown as { posthog?: typeof ph }).posthog = ph;
        },
      });
    } else {
      posthog.opt_in_capturing();
    }
    client = posthog;
    for (const { event, properties } of pendingEvents.splice(0)) {
      posthog.capture(event, properties);
    }
    return posthog;
  });
  loading = task;
  const clearLoading = () => {
    if (loading === task) loading = null;
  };
  void task.then(clearLoading, clearLoading);
  return task;
}

/** Inicializa PostHog en el navegador (idempotente). No-op en SSR. */
export function initPostHog() {
  if (typeof window === 'undefined' || !hasAnalyticsConsent()) return;
  void loadPostHog().catch(() => {
    // An adblocker or a failed analytics chunk must not affect the page.
  });
}

/** Captura un pageview manual (se llama en cada cambio de ruta del App Router). */
export function capturePageview(url?: string) {
  if (typeof window === 'undefined' || !hasAnalyticsConsent()) return;
  const properties = url ? { $current_url: url } : undefined;
  if (client) client.capture('$pageview', properties);
  else pendingEvents.push({ event: '$pageview', properties });
}

/** Captura un evento en PostHog (usado en paralelo a GTM en analytics.ts). */
export function capture(event: string, properties?: Record<string, unknown>) {
  if (typeof window === 'undefined' || !hasAnalyticsConsent()) return;
  if (client) client.capture(event, properties);
  else pendingEvents.push({ event, properties });
}

/** Stops future captures when consent is withdrawn. Browser storage is cleared by the consent layer. */
export function disablePostHog() {
  pendingEvents.splice(0);
  if (typeof window === 'undefined' || !client) return;
  try {
    client.reset();
    client.opt_out_capturing();
  } catch {
    /* silenciar: adblocker u otro */
  }
}
